/*
 * @Author: liuhang liuhang@nextmar.com
 * @Date: 2025-07-23 17:00:00
 * @Description: Try-on state management slice - based on Vue tryOn.js store
 */
import { tryOnApi, UploadImageData, CustomerImageData } from '@/api/tryonApi';
import { exitPageFullscreen } from '@/utils/fullscreen';
import logger from '@/utils/client-logger';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import { getArTryOnModelUrl } from '@/domain/tryon/model';
import { StateCreator } from 'zustand';

// 模特数据接口
export interface ModelData {
  sImg: string;
  bImg: string;
  data: {
    photo_original_width: number;
    photo_original_height: number;
    landmark72_0_x: number;
    landmark72_0_y: number;
    landmark72_12_x: number;
    landmark72_12_y: number;
    landmark72_21_x: number;
    landmark72_21_y: number;
    landmark72_38_x: number;
    landmark72_38_y: number;
  };
  startTime?: number;
  user?: boolean; // 标记是否为用户上传的图片
  id?: string | number; // API返回的ID
  saved?: boolean; // 标记是否为已保存的图片
}

export interface TryonState {
  // 试戴弹窗显示状态
  tryonShow: boolean;
  // 试戴类型: 1-图片试戴, 2-VR试戴
  tryonType: 1 | 2;
  // 当前试戴的商品
  tryonProduct: any | null;
  // 提示信息是否显示
  tryonShowTip: boolean;
  // 分享弹窗是否显示
  tryonShowShare: boolean;
  // 截屏弹窗是否显示
  tryonShowCapture: boolean;
  // 试戴失败弹窗是否显示
  tryonShowFailed: boolean;
  // 相关商品列表
  tryonRelatedProducts: any[];
  // 当前选中的商品索引
  tryonCurrentIndex: number;

  // 来自Vue store的状态
  // 模特列表（包含预设模特和用户上传的模特）
  modelList: ModelData[];
  // 普通试戴数据
  normalTry: string;
  // 试戴商品信息
  tryOnProductInfo: any;
}

export interface TryonActions {
  // 原有的设置方法
  setTryonShow: (show: boolean) => void;
  setTryonType: (type: 1 | 2) => void;
  setTryonProduct: (product: any) => void;
  setShowTip: (show: boolean) => void;
  setShowShare: (show: boolean) => void;
  setShowCapture: (show: boolean) => void;
  setShowFailed: (show: boolean) => void;
  setRelatedProducts: (products: any[]) => void;
  setCurrentIndex: (index: number) => void;
  startTryon: (product: any) => void;
  closeTryon: () => void;
  switchTryonType: (type: 1 | 2) => void;
  resetTryon: () => void;
  // 业务方法
  handleTryon: (product: any, inAppH5?: boolean) => Promise<void>;

  // 来自Vue store的方法
  // 模特列表管理
  setModelList: (model: ModelData) => void;
  delModelList: (model: ModelData) => void;
  setNormalTry: (data: string) => void;
  setTryOnProductInfo: (data: any) => void;
  // API方法
  delTryOnList: (params: any) => Promise<any>;
  // 新增的API方法
  uploadTryOnImg: (data: UploadImageData) => Promise<any>;
  uploadUrlTryOnImg: (imageUrl: string, additionalData?: any) => Promise<any>;
  getTryOnImages: (CustomerImageData) => Promise<any>;
  saveTryOnImage: (image: File | Blob, additionalData?: any) => Promise<any>;
  getTryOnSkuInfo: (sku: string) => Promise<any>;
}

export type TryonSlice = TryonState & TryonActions;

// 初始化的模特列表（来自Vue store）
const initialModelList: ModelData[] = [
  {
    sImg: '/tryOn/model_1.jpg',
    bImg: '/tryOn/big_1.jpg',
    data: {
      photo_original_width: 600,
      photo_original_height: 600,
      landmark72_0_x: 153.2,
      landmark72_0_y: 264.71,
      landmark72_12_x: 437.05,
      landmark72_12_y: 261.92,
      landmark72_21_x: 230.08,
      landmark72_21_y: 262.1,
      landmark72_38_x: 360.9,
      landmark72_38_y: 260.48,
    },
  },
  {
    sImg: '/tryOn/model_2.jpg',
    bImg: '/tryOn/big_2.jpg',
    data: {
      photo_original_width: 600,
      photo_original_height: 600,
      landmark72_0_x: 161.76,
      landmark72_0_y: 258.71,
      landmark72_12_x: 454.83,
      landmark72_12_y: 250.74,
      landmark72_21_x: 233.09,
      landmark72_21_y: 247.28,
      landmark72_38_x: 375.47,
      landmark72_38_y: 243.41,
    },
  },
  {
    sImg: '/tryOn/model_3.jpg',
    bImg: '/tryOn/big_3.jpg',
    data: {
      photo_original_width: 600,
      photo_original_height: 600,
      landmark72_0_x: 147.48,
      landmark72_0_y: 274.97,
      landmark72_12_x: 437.76,
      landmark72_12_y: 264.06,
      landmark72_21_x: 223.18,
      landmark72_21_y: 270.01,
      landmark72_38_x: 358.979,
      landmark72_38_y: 264.98,
    },
  },
  {
    sImg: '/tryOn/model_4.jpg',
    bImg: '/tryOn/big_4.jpg',
    data: {
      photo_original_width: 600,
      photo_original_height: 600,
      landmark72_0_x: 139.95,
      landmark72_0_y: 278.57,
      landmark72_12_x: 456.7,
      landmark72_12_y: 274.23,
      landmark72_21_x: 229.57,
      landmark72_21_y: 272.16,
      landmark72_38_x: 374.39,
      landmark72_38_y: 270.44,
    },
  },
  {
    sImg: '/tryOn/model_5.jpg',
    bImg: '/tryOn/big_5.jpg',
    data: {
      photo_original_width: 600,
      photo_original_height: 600,
      landmark72_0_x: 149.46,
      landmark72_0_y: 259.92,
      landmark72_12_x: 444.67,
      landmark72_12_y: 262.95,
      landmark72_21_x: 229.38,
      landmark72_21_y: 259.98,
      landmark72_38_x: 365.4,
      landmark72_38_y: 260.44,
    },
  },
  {
    sImg: '/tryOn/model_6.jpg',
    bImg: '/tryOn/big_6.jpg',
    data: {
      photo_original_width: 600,
      photo_original_height: 600,
      landmark72_0_x: 164.91,
      landmark72_0_y: 275.24,
      landmark72_12_x: 450.44,
      landmark72_12_y: 269.02,
      landmark72_21_x: 239.69,
      landmark72_21_y: 270.71,
      landmark72_38_x: 373.99,
      landmark72_38_y: 268.03,
    },
  },
  {
    sImg: '/tryOn/model_7.jpg',
    bImg: '/tryOn/big_7.jpg',
    data: {
      photo_original_width: 600,
      photo_original_height: 600,
      landmark72_0_x: 176.46,
      landmark72_0_y: 263.98,
      landmark72_12_x: 482.37,
      landmark72_12_y: 263.16,
      landmark72_21_x: 266.28,
      landmark72_21_y: 269.23,
      landmark72_38_x: 403.31,
      landmark72_38_y: 268.66,
    },
  },
  {
    sImg: '/tryOn/model_8.jpg',
    bImg: '/tryOn/big_8.jpg',
    data: {
      photo_original_width: 600,
      photo_original_height: 600,
      landmark72_0_x: 155.86,
      landmark72_0_y: 253.7,
      landmark72_12_x: 464.1,
      landmark72_12_y: 251.26,
      landmark72_21_x: 237.79,
      landmark72_21_y: 276.04,
      landmark72_38_x: 375.1,
      landmark72_38_y: 274.65,
    },
  },
  {
    sImg: '/tryOn/model_9.jpg',
    bImg: '/tryOn/big_9.jpg',
    data: {
      photo_original_width: 600,
      photo_original_height: 600,
      landmark72_0_x: 167.8,
      landmark72_0_y: 292.8,
      landmark72_12_x: 461.1,
      landmark72_12_y: 289.28,
      landmark72_21_x: 246.77,
      landmark72_21_y: 269.14,
      landmark72_38_x: 380.91,
      landmark72_38_y: 267.03,
    },
  },
  {
    sImg: '/tryOn/model_10.jpg',
    bImg: '/tryOn/big_10.jpg',
    data: {
      photo_original_width: 600,
      photo_original_height: 600,
      landmark72_0_x: 159.48,
      landmark72_0_y: 274.86,
      landmark72_12_x: 460.96,
      landmark72_12_y: 273.09,
      landmark72_21_x: 239.29,
      landmark72_21_y: 275.94,
      landmark72_38_x: 376.7,
      landmark72_38_y: 274.53,
    },
  },
  {
    sImg: '/tryOn/model_11.jpg',
    bImg: '/tryOn/big_11.jpg',
    data: {
      photo_original_width: 600,
      photo_original_height: 600,
      landmark72_0_x: 161.18,
      landmark72_0_y: 289.16,
      landmark72_12_x: 449.43,
      landmark72_12_y: 285.68,
      landmark72_21_x: 251.58,
      landmark72_21_y: 271.88,
      landmark72_38_x: 375.62,
      landmark72_38_y: 270.82,
    },
  },
  {
    sImg: '/tryOn/model_12.jpg',
    bImg: '/tryOn/big_12.jpg',
    data: {
      photo_original_width: 600,
      photo_original_height: 600,
      landmark72_0_x: 159.56,
      landmark72_0_y: 268.93,
      landmark72_12_x: 440.38,
      landmark72_12_y: 265.14,
      landmark72_21_x: 235.05,
      landmark72_21_y: 275.11,
      landmark72_38_x: 361.07,
      landmark72_38_y: 272.83,
    },
  },
];

const initialTryonState: TryonState = {
  tryonShow: false,
  tryonType: 1,
  tryonProduct: null,
  tryonShowTip: false,
  tryonShowShare: false,
  tryonShowCapture: false,
  tryonShowFailed: false,
  tryonRelatedProducts: [],
  tryonCurrentIndex: 0,

  // Vue store状态
  modelList: initialModelList,
  normalTry: '',
  tryOnProductInfo: {},
};

export const createTryonSlice: StateCreator<any, [], [], TryonSlice> = (set, get) => ({
  ...initialTryonState,

  // 原有方法
  setTryonShow: (show) =>
    set((state) => ({
      ...state,
      tryonShow: show,
      ...(show === false && {
        tryonShowTip: false,
        tryonShowShare: false,
        tryonShowCapture: false,
        tryonShowFailed: false,
      }),
    })),

  setTryonType: (type) =>
    set((state) => ({
      ...state,
      tryonType: type,
    })),

  setTryonProduct: (product) =>
    set((state) => ({
      ...state,
      tryonProduct: product,
    })),

  setShowTip: (showTip) =>
    set((state) => ({
      ...state,
      tryonShowTip: showTip,
    })),

  setShowShare: (showShare) =>
    set((state) => ({
      ...state,
      tryonShowShare: showShare,
    })),

  setShowCapture: (showCapture) =>
    set((state) => ({
      ...state,
      tryonShowCapture: showCapture,
    })),

  setShowFailed: (showFailed) =>
    set((state) => ({
      ...state,
      tryonShowFailed: showFailed,
    })),

  setRelatedProducts: (relatedProducts) =>
    set((state) => ({
      ...state,
      tryonRelatedProducts: relatedProducts,
    })),

  setCurrentIndex: (currentIndex) =>
    set((state) => ({
      ...state,
      tryonCurrentIndex: currentIndex,
    })),

  startTryon: (product) =>
    set((state) => {
      const type = getArTryOnModelUrl(product) ? 2 : 1;
      return {
        ...state,
        tryonShow: true,
        tryonProduct: product,
        tryonType: type,
      };
    }),

  closeTryon: () => {
    void exitPageFullscreen();
    set((state) => ({
      ...state,
      tryonShow: false,
    }));
  },

  switchTryonType: (type) =>
    set((state) => ({
      ...state,
      tryonType: type,
      tryonShowTip: false,
      tryonShowShare: false,
      tryonShowCapture: false,
    })),

  resetTryon: () =>
    set((state) => ({
      ...state,
      ...initialTryonState,
    })),

  // 试戴业务逻辑 - 从 ProductCard 抽离
  handleTryon: async (product, inAppH5 = false) => {
    let imgTryon = false;
    let arTryOn = false;

    const { setTryOnProductInfo, setNormalTry, startTryon } = get();

    try {
      // app 内嵌页面，直接调用app的试戴
      // TODO '@/common/app-communication-manager' 里面没有实现试戴方法，这里就单独使用啦
      if (inAppH5) {
        window[EAppCommunicationFuncName.PRODUCT_TRY_ON].postMessage(
          JSON.stringify({
            product_id: product.id,
            is_ar: getArTryOnModelUrl(product) ? '1' : '0',
          }),
        );
        return;
      }
      // 设置产品信息到store
      setTryOnProductInfo(product);
      // 处理图片试戴
      if (product.tryon_image_url) {
        setNormalTry(product.tryon_image_url);
        imgTryon = true;
      }
      // 处理AR试戴（GLB 由试戴页 ARTryOn 加载，不再预取 vrJson）
      if (getArTryOnModelUrl(product)) {
        arTryOn = true;
        if (typeof window !== 'undefined' && (window as any).gtag) {
          (window as any).gtag('event', 'tryon', {
            event_label: 'Tryon',
            event_category: 'PRODUCT_LIST',
            productId: product.id,
            productName: product.name,
          });
        }
      }

      // 启动试戴界面
      if (arTryOn) {
        startTryon({ ...product, tryonType: 2 }); // AR试戴
      } else if (imgTryon) {
        startTryon({ ...product, tryonType: 1 }); // 图片试戴
      }
    } catch (error) {
      logger.error('Handle tryon error:', error);
    }
  },

  // Vue store方法实现
  setModelList: (model) =>
    set((state) => {
      const newModel = { ...model, startTime: Date.now() };
      const newModelList = [newModel, ...state.modelList];
      // 保存到localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('zl-modelList', JSON.stringify(newModelList));
      }
      return {
        ...state,
        modelList: newModelList,
      };
    }),

  delModelList: (model) =>
    set((state) => {
      const newModelList = state.modelList.filter((item) => item !== model);
      // 更新localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('zl-modelList', JSON.stringify(newModelList));
      }
      return {
        ...state,
        modelList: newModelList,
      };
    }),

  setNormalTry: (data) =>
    set((state) => ({
      ...state,
      normalTry: data,
    })),

  setTryOnProductInfo: (data) =>
    set((state) => ({
      ...state,
      tryOnProductInfo: data,
    })),

  // API方法
  delTryOnList: async (params) => {
    try {
      const response = await tryOnApi.delTryOnList(params);
      return response.data;
    } catch {
      return null;
    }
  },

  // 新增的API方法
  uploadTryOnImg: async (data: UploadImageData) => {
    try {
      const response = await tryOnApi.uploadTryOnImg(data);
      return response.data;
    } catch {
      return null;
    }
  },

  uploadUrlTryOnImg: async (imageUrl: string, additionalData?: any) => {
    try {
      const data = { imageUrl, ...additionalData };
      const response = await tryOnApi.uploadUrlTryOnImg(data);
      return response.data;
    } catch {
      return null;
    }
  },

  getTryOnImages: async (data: CustomerImageData) => {
    try {
      const response = await tryOnApi.getTryOn(data);
      return response.data;
    } catch {
      return null;
    }
  },

  saveTryOnImage: async (image: File | Blob, additionalData?: any) => {
    try {
      const data = { image, ...additionalData };
      const response = await tryOnApi.saveTryOn(data);
      return response.data;
    } catch {
      return null;
    }
  },

  getTryOnSkuInfo: async (sku: string) => {
    try {
      const data = { sku };
      const response = await tryOnApi.tryOn(data);
      return response.data;
    } catch {
      return null;
    }
  },
});
